import { getLiferayFetchInit } from "./auth";

const DEFAULT_PAGE_SIZE = 200;
const DRL_BASE_URL =
  process.env.REACT_APP_BASE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

async function fetchPicklistWithAdmin(url) {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      // "x-csrf-token": window.Liferay?.authToken || "",
      "Authorization": "Basic " + btoa("prabhudasu:root"),
      "Accept": "application/json",
      "Content-Type": "application/json"
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

async function fetchPicklistWithSession(url) {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      // "x-csrf-token": window.Liferay?.authToken || "",
      "Authorization": "Basic " + btoa("prabhudasu:root"),
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

function getFetchConfig() {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const csrf = window.Liferay?.authToken;
  if (csrf) {
    headers["x-csrf-token"] = csrf;
  }

  return {
    headers,
    credentials: "include",
  };
}

function buildCollectionUrl(objectName, { filter, page, pageSize, sort } = {}) {
  const params = new URLSearchParams();

  if (page) {
    params.set("page", String(page));
  }

  if (pageSize) {
    params.set("pageSize", String(pageSize));
  }

  if (sort) {
    params.set("sort", sort);
  }

  if (filter) {
    params.set("filter", filter);
  }

  const queryString = params.toString();
  return queryString
    ? `/o/c/${objectName}?${queryString}&restrictFields=actions`
    : `/o/c/${objectName}?restrictFields=actions`;
}

async function fetchJson(url) {
  const response = await fetch(url, getFetchConfig());

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

async function fetchJsonWithLiferayAuth(url) {
  const init = await getLiferayFetchInit({ method: "GET" });
  const response = await fetch(url, init);

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

async function fetchAllObjectEntries(objectName, options = {}) {
  const useLiferayAuth = Boolean(options.useLiferayAuth);
  const fetchImpl = useLiferayAuth ? fetchJsonWithLiferayAuth : fetchJson;
  let currentPage = 1;
  let lastPage = 1;
  const allItems = [];

  do {
    const url = buildCollectionUrl(objectName, {
      page: currentPage,
      pageSize: options.pageSize || DEFAULT_PAGE_SIZE,
      filter: options.filter || "",
      sort: options.sort || "",
    });

    const data = await fetchImpl(url);
    const items = Array.isArray(data.items) ? data.items : [];

    allItems.push(...items);
    lastPage = Number(data.lastPage || 1);
    currentPage += 1;
  } while (currentPage <= lastPage);

  return allItems;
}


async function fetchPicklistEntries(externalReferenceCode) {
  try {
    const definitionUrl = `/o/headless-admin-list-type/v1.0/list-type-definitions/by-external-reference-code/${externalReferenceCode}`;

    let definition = null;
    try {
      definition = await fetchPicklistWithSession(definitionUrl);
    } catch (sessionError) {
      console.warn("Session picklist fetch failed, trying admin auth:", sessionError);
      definition = await fetchPicklistWithAdmin(definitionUrl);
    }

    if (!definition || !definition.id) {
      return [];
    }

    if (Array.isArray(definition.listTypeEntries) && definition.listTypeEntries.length) {
      return definition.listTypeEntries;
    }

    let currentPage = 1;
    let lastPage = 1;
    const allItems = [];

    do {
      const url = `/o/headless-admin-list-type/v1.0/list-type-definitions/${definition.id}/list-type-entries?page=${currentPage}&pageSize=${DEFAULT_PAGE_SIZE}`;

      let data = null;
      try {
        data = await fetchPicklistWithSession(url);
      } catch (sessionEntriesError) {
        console.warn("Session picklist entries fetch failed, trying admin auth:", sessionEntriesError);
        data = await fetchPicklistWithAdmin(url);
      }

      if (!data) break;

      const items = Array.isArray(data.items) ? data.items : [];
      allItems.push(...items);

      lastPage = Number(data.lastPage || 1);
      currentPage++;
    } while (currentPage <= lastPage);

    return allItems;
  } catch (error) {
    console.error("Picklist fetch failed:", error);
    return [];
  }
}
function sortByLabel(options) {
  return [...options].sort((left, right) =>
    String(left.label || "").localeCompare(String(right.label || ""))
  );
}

function uniqueByValue(options) {
  const map = new Map();
  (options || []).forEach((item) => {
    const key = String(item?.value || "").trim();
    if (!key) return;
    if (!map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
}

export async function fetchCasteCategoryOptions() {
  const items = await fetchAllObjectEntries("castecategories", {
    useLiferayAuth: true,
  });

  return sortByLabel(
    items
      .filter(
        (item) =>
          item?.status?.code === 0 &&
          item?.isActive !== false &&
          item?.name
      )
      .map((item) => ({
        value: String(item.id),
        label: String(item.name).trim(),
        casteCategoryId: String(item.casteCategoryId || item.id),
      }))
  );
}

export async function fetchCasteOptions(casteCategoryId) {
  if (!casteCategoryId) return [];

  const casteCategoryIdClean = String(casteCategoryId).trim();
  if (!casteCategoryIdClean) return [];

  const items = await fetchAllObjectEntries("castes", {
    filter: `r_casteCategory_c_casteCategoryId eq '${casteCategoryIdClean}'`,
    sort: "name:asc",
    useLiferayAuth: true,
  });

  return sortByLabel(
    items
      .filter((item) => item?.name)
      .map((item) => ({
        value: String(item.id),
        label: String(item.name).trim(),
      }))
  );
}


export async function fetchCastesByReligion(religionId) {
  if (!religionId) return [];

  const religionIdClean = String(religionId).trim();
  if (!religionIdClean) return [];

  async function resolveReligionMasterId(value) {
    const v = String(value || "").trim();
    if (!v) return null;

    if (/^\d+$/.test(v)) return v;

    const escaped = v.replace(/'/g, "''");
    const filters = [
      `name eq '${escaped}'`,
      `religionname eq '${escaped}'`,
      `religionName eq '${escaped}'`,
      `code eq '${escaped}'`,
    ];

    for (const filter of filters) {
      try {
        const items = await fetchAllObjectEntries("religionmasters", {
          filter,
          sort: "name:asc",
        });
        if (Array.isArray(items) && items.length > 0) {
          const id = items[0]?.id;
          if (id != null) return String(id).trim();
        }
      } catch (e) {
        console.log("error:::::::::",e)
      }
    }

    return null;
  }

  const resolvedReligionMasterId = await resolveReligionMasterId(
    religionIdClean
  );
  const casteRelationReligionId = resolvedReligionMasterId || religionIdClean;

  const candidateFilters = [
    `r_religion_c_c_religionMasterId eq '${casteRelationReligionId}'`,
    `r_religion_c_religionMasterId eq '${casteRelationReligionId}'`,
    `r_religion_c_religionMasterid eq '${casteRelationReligionId}'`,
    `r_religion_c_religionId eq '${casteRelationReligionId}'`,
    `r_religion_c_id eq '${casteRelationReligionId}'`,
    `religionMasterId eq '${casteRelationReligionId}'`,
    `religionId eq '${casteRelationReligionId}'`,
  ];

  const extractCasteCategoryId = (item) => {
    const fromDirect =
      item?.casteCategoryId ??
      item?.castecategoryid ??
      item?.casteCategoryMasterId ??
      item?.castecategorymasterid;

    const fromRelation =
      item?.r_casteCategory_c_casteCategoryId ??
      item?.r_casteCategory_c_casteCategoryIdMasterId ??
      item?.r_casteCategory_c_casteCategoryMasterId ??
      item?.r_casteCategory_c_casteCategoryMasterid ??
      item?.r_casteCategory_c_id;

    const val = fromDirect ?? fromRelation ?? null;
    return val == null ? null : String(val).trim();
  };

  for (const filter of candidateFilters) {
    try {
      const items = await fetchAllObjectEntries("castes", {
        filter,
        sort: "name:asc",
      });

      const mappedRaw = (items || [])
        .map((item) => {
          const categoryId = extractCasteCategoryId(item);
          return {
            value: String(item?.id ?? "").trim(),
            label: String(item?.name ?? "").trim(),
            casteCategoryId: categoryId,
          };
        })
        .filter((x) => x.value && x.label);

      if (mappedRaw.length > 0) return mappedRaw;
    } catch (e) {
      console.warn("fetchCastesByReligion candidate failed:", filter, e?.message || e);
    }
  }

  return [];
}

export async function fetchGenderOptions() {
  const items = await fetchPicklistEntries("DBT-GENDER-PICKLIST");

  return sortByLabel(
    items.map((item) => ({
      value: String(item.key || item.name),
      label: String(item.name || item.key || ""),
    }))
  );
}

export async function fetchReligionOptions() {
  const items = await fetchPicklistEntries("DBT-RELIGION-PICKLIST");

  return sortByLabel(
    items.map((item) => ({
      value: String(item.key || item.name),
      label: String(item.name || item.key || ""),
    }))
  );
}

export async function fetchStateOptions() {
  const items = await fetchAllObjectEntries("states", { sort: "name:asc" });
  return sortByLabel(
    (items || [])
      .filter((item) => item?.name)
      .map((item) => ({
        value: String(item.id),
        label: String(item.name).trim(),
      }))
  );
}

export async function fetchDistrictOptionsByState(stateId) {
  if (!stateId) return [];
  const stateIdClean = String(stateId).trim();
  if (!stateIdClean) return [];

  const items = await fetchAllObjectEntries("districts", {
    filter: `r_state_c_stateId eq '${stateIdClean}'`,
    sort: "name:asc",
  });

  return sortByLabel(
    (items || [])
      .filter((item) => item?.name)
      .map((item) => ({
        value: String(item.id),
        label: String(item.name).trim(),
      }))
  );
}

export async function fetchTalukaOptionsByDistrict(districtId) {
  if (!districtId) return [];
  const districtIdClean = String(districtId).trim();
  if (!districtIdClean) return [];

  const items = await fetchAllObjectEntries("talukas", {
    filter: `r_district_c_districtId eq '${districtIdClean}'`,
    sort: "name:asc",
  });

  return sortByLabel(
    (items || [])
      .filter((item) => item?.name)
      .map((item) => ({
        value: String(item.id),
        label: String(item.name).trim(),
      }))
  );
}

export async function fetchUserAadhaarProfileByEmail(email) {
  try {
    const normalizedEmail = String(email || "").trim().toLowerCase();

    if (!normalizedEmail) {
      return null;
    }

    const externalReferenceCode = `${normalizedEmail}-aadharprofile`;
    const response = await fetch(
      `/o/c/useraadharprofiles/by-external-reference-code/${encodeURIComponent(externalReferenceCode)}`,
      getFetchConfig(),
    );

    if (!response.ok) {
      if (response.status !== 404) {
        console.warn(
          "Failed to fetch user Aadhaar profile:",
          response.status,
          externalReferenceCode,
        );
      }
      return null;
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching user Aadhaar profile by email:", error);
    return null;
  }
}

function escapeOdataContains(value) {
  return String(value || "").replace(/'/g, "''");
}

export async function fetchInstitutionOptions({
  search,
  stateId,
  districtId,
  talukaId,
  pageSize = 20,
} = {}) {
  const q = String(search || "").trim();
  const talukaIdClean = String(talukaId || "").trim();
  const districtIdClean = String(districtId || "").trim();
  const stateIdClean = String(stateId || "").trim();

  if (talukaIdClean) {
    const talukaFilter = `r_talukaCollege_c_talukaId eq '${talukaIdClean}'`;
    const hasSearch = q.length >= 2;
    const containsFilter = hasSearch
      ? `contains(name,'${escapeOdataContains(q)}')`
      : null;
    const finalFilter = containsFilter
      ? `${containsFilter} and ${talukaFilter}`
      : talukaFilter;

    const url = buildCollectionUrl("colleges", {
      page: 1,
      pageSize,
      filter: finalFilter,
      sort: "name:asc",
    });

    const data = await fetchJson(url);
    const items = Array.isArray(data?.items) ? data.items : [];

    const mapped = uniqueByValue(
      items
        .filter((item) => item?.name)
        .map((item) => ({
          value: String(item.id),
          label: String(item.name).trim(),
        }))
    );

    return sortByLabel(mapped);
  }

  if (q.length < 2) return [];

  const filters = [];
  filters.push(`contains(name,'${escapeOdataContains(q)}')`);

  const relationCandidates = [];
  if (districtIdClean) {
    relationCandidates.push(
      `r_district_c_districtMasterId eq '${districtIdClean}'`,
      `r_district_c_districtMasterid eq '${districtIdClean}'`,
      `r_district_c_districtId eq '${districtIdClean}'`,
      `r_district_c_id eq '${districtIdClean}'`
    );
  } else if (stateIdClean) {
    relationCandidates.push(
      `r_state_c_stateMasterId eq '${stateIdClean}'`,
      `r_state_c_stateMasterid eq '${stateIdClean}'`,
      `r_state_c_stateId eq '${stateIdClean}'`,
      `r_state_c_id eq '${stateIdClean}'`
    );
  }

  const combinedBase = filters.join(" and ");
  const tryFilters = relationCandidates.length
    ? relationCandidates.map((rel) => `${combinedBase} and ${rel}`)
    : [combinedBase];

  for (const filter of tryFilters) {
    try {
      const url = buildCollectionUrl("colleges", {
        page: 1,
        pageSize,
        filter,
        sort: "name:asc",
      });
      const data = await fetchJson(url);
      const items = Array.isArray(data?.items) ? data.items : [];
      const mapped = uniqueByValue(
        items
          .filter((item) => item?.name)
          .map((item) => ({
            value: String(item.id),
            label: String(item.name).trim(),
          }))
      );
      if (mapped.length > 0) return sortByLabel(mapped);
    } catch (error) {
      console.warn("Institution fetch filter failed:", filter, error);
    }
  }

  return [];
}

async function fetchCurrentCourseOptions(collegeId) {
  if (!collegeId) return [];

  const collegeIdClean = String(collegeId).trim();
  const signedIn = Boolean(window.Liferay?.ThemeDisplay?.isSignedIn?.());

  try {
  
    const mappingInit = signedIn ? await getLiferayFetchInit({ method: "GET" }) : getFetchConfig();
    const mappingResponse = await fetch(
      `/o/c/collegecoursemappings/?page=1&sort=courseId:asc&search=${encodeURIComponent(
        collegeIdClean
      )}`,
      mappingInit
    );

    if (!mappingResponse.ok) {
      throw new Error(`Failed to fetch college course mappings: ${mappingResponse.status}`);
    }

    const mappingData = await mappingResponse.json();
    const mappings = Array.isArray(mappingData?.items) ? mappingData.items : [];

    const relatedCourseIds = [
      ...new Set(
        (mappings || [])
          .map(
            (item) =>
              item?.r_course_c_courseId ??
              item?.r_course_c_courseid ??
              item?.courseId
          )
          .filter((id) => id !== null && id !== undefined && String(id).trim() !== "")
          .map((id) => String(id).trim())
      ),
    ];

    if (relatedCourseIds.length > 0) {
      const courseRows = await Promise.all(
        relatedCourseIds.map(async (id) => {
          try {
            return await fetchAllObjectEntries("courses", {
              filter: `id eq '${id}'`,
              useLiferayAuth: signedIn,
            });
          } catch {
            return [];
          }
        })
      );

      const normalized = uniqueByValue(
        courseRows.flat().map((item) => ({
          value: String(item?.id ?? "").trim(),
          label: String(
            item?.name || item?.courseName || item?.programName || item?.streamName || ""
          ).trim(),
        }))
      ).filter((item) => item.value && item.label);

      if (normalized.length > 0) {
        return sortByLabel(normalized);
      }
    }
  } catch (error) {
    console.warn("Course mapping lookup failed, falling back to coursemasters:", error);
  }

  const relationFilters = [
    `r_collegeSchool_c_collegeAndSchoolMasterId eq '${collegeIdClean}'`,
    `r_collegeSchool_c_collegeandschoolmasterid eq '${collegeIdClean}'`,
    `r_collegeSchool_c_id eq '${collegeIdClean}'`,
  ];

  for (const relationFilter of relationFilters) {
    try {
      const items = await fetchAllObjectEntries("coursemasters", {
        filter: relationFilter,
        sort: "courseName:asc",
        useLiferayAuth: signedIn,
      });

      const mapped = uniqueByValue(
        (items || []).map((item) => ({
          value: String(item?.id ?? "").trim(),
          label: String(item?.courseName || item?.name || "").trim(),
        }))
      ).filter((item) => item.value && item.label);

      if (mapped.length > 0) {
        return sortByLabel(mapped);
      }
    } catch (error) {
      console.warn("coursemasters filter failed:", relationFilter, error);
    }
  }

  return [];
}

async function fetchCollegeIdByInstitutionName(name) {
  const query = String(name || "").trim();
  if (query.length < 2) return null;

  const escaped = query.replace(/'/g, "''");
  try {
    const url = `/o/c/colleges?page=1&pageSize=20&filter=contains(name,'${escaped}')&restrictFields=actions`;
    const data = await fetchJson(url);
    const items = Array.isArray(data?.items) ? data.items : [];
    if (items.length === 0) return null;

    const lower = query.toLowerCase();
    const exact = items.find(
      (item) => String(item?.name || "").trim().toLowerCase() === lower
    );
    const chosen = exact || items[0];
    return chosen?.id != null ? String(chosen.id).trim() : null;
  } catch (error) {
    console.warn("College name lookup failed:", error);
    return null;
  }
}


export async function fetchCoursesForInstitutionName(institutionName) {
  const raw = String(institutionName || "").trim();
  if (!raw) return [];

  const collegeId = await fetchCollegeIdByInstitutionName(raw);
  if (collegeId) {
    const byId = await fetchCurrentCourseOptions(collegeId);
    if (byId.length > 0) return byId;
  }

  return fetchCurrentCourseOptions(raw);
}

export async function fetchCoursesForCollegeId(collegeId) {
  if (!collegeId) return [];
  return fetchCurrentCourseOptions(collegeId);
}

function normalizeString(value) {
  return String(value || "").trim();
}

function parseJsonSafe(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeDrlContent(drlContent) {
  if (typeof drlContent !== "string") return "";
  return drlContent
    .replace(/\\"/g, '"')
    .replace(/\\r\\n/g, "\r\n")
    .replace(/\\n/g, "\n")
    .replace(/\\\\/g, "\\")
    .trim();
}

function isDrlValidationSuccessful(result) {
  const nestedResult = result?.data || result?.result || result?.payload || null;
  return result?.valid === true || nestedResult?.valid === true;
}

function extractStandardGradeValue(value) {
  const normalized = normalizeString(value);
  if (!normalized) return "";

  const match = normalized.match(/\d+/);
  return match ? match[0] : "";
}

export function mapApplicantPayload(formData) {
  const applicant = {};

  const assignString = (key, value) => {
    const normalized = normalizeString(value);
    if (normalized !== "") {
      applicant[key] = normalized;
    }
  };

  const assignNumber = (key, value) => {
    if (value === null || value === undefined || String(value).trim() === "") {
      return;
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      applicant[key] = numericValue;
    }
  };

  assignString("domicileOfMaharashtra", formData?.domicileOfMaharashtra);
  assignString("religion", formData?.religion);
  assignString("casteCategory", formData?.casteCategory);
  assignString("casteCategoryLabel", formData?.casteCategoryLabel);
  assignString("beneficiaryCategory", formData?.beneficiaryCategory);
  assignString("caste", formData?.caste);
  assignString("gender", formData?.gender);
  assignString("disability", formData?.disability);
  assignNumber("familyAnnualIncome", formData?.familyAnnualIncome);
  assignString("alpaBhuOrRegisteredLabour", formData?.alpaBhuOrRegisteredLabour);
  assignString("hazardousOrUncleanOccupation", formData?.hazardousOrUncleanOccupation);
  assignString("birthOrder", formData?.birthOrder);
  assignString("birthOrderLabel", formData?.birthOrderLabel);
  assignString("grade10CompletedFromMaharashtra", formData?.grade10CompletedFromMaharashtra);
  assignString("admissionThroughCET", formData?.admissionThroughCET);
  assignString(
    "completed10thFromGovernmentVidyaniketan",
    formData?.completed10thFromGovernmentVidyaniketan
  );
  assignString("institutionName", formData?.institutionName);
  assignString("universityName", formData?.universityName);
  assignString("currentCourseName", formData?.currentCourseName);
  assignString("currentCourseNameLabel", formData?.currentCourseNameLabel);
  assignString("courseType", formData?.courseType);
  assignString("courseTypeLabel", formData?.courseTypeLabel);
  assignString("yearOfStudyOrCourseLevel", formData?.yearOfStudyOrCourseLevel);
  assignString(
    "yearOfStudyOrCourseLevelLabel",
    formData?.yearOfStudyOrCourseLevelLabel,
  );
  assignString("stateId", formData?.stateId);
  assignString("stateLabel", formData?.stateLabel);
  assignString("districtId", formData?.districtId);
  assignString("districtLabel", formData?.districtLabel);
  assignString("talukaId", formData?.talukaId);
  assignString("talukaLabel", formData?.talukaLabel);
  const selectedSchemeTypes = Array.isArray(formData?.schemeType)
    ? formData.schemeType.map((value) => normalizeSchemeTypeToken(value)).filter(Boolean)
    : [];
  if (selectedSchemeTypes.length === 1) {
    applicant.schemeType = selectedSchemeTypes[0];
  }
  const standardGradeValue =
    extractStandardGradeValue(applicant.yearOfStudyOrCourseLevelLabel) ||
    extractStandardGradeValue(applicant.yearOfStudyOrCourseLevel);

  return {
    ...applicant,
    // lower-case aliases for flexible rule engines
    ...(applicant.domicileOfMaharashtra
      ? {
          domicileofmaharashtra: applicant.domicileOfMaharashtra,
          areYouDomicileOfMaharashtraMaharashtraKarnatakaBorder:
            applicant.domicileOfMaharashtra,
          areyoudomicileofmaharashtramaharashtrakarnatakaborder:
            applicant.domicileOfMaharashtra,
        }
      : {}),
    ...(applicant.casteCategory
      ? {
          castecategory: applicant.casteCategory,
        }
      : {}),
    ...(applicant.casteCategoryLabel
      ? {
          castecategorylabel: applicant.casteCategoryLabel,
          casteCategoryLabel: applicant.casteCategoryLabel,
        }
      : {}),
    ...(applicant.beneficiaryCategory
      ? {
          beneficiarycategory: applicant.beneficiaryCategory,
          hosteller: applicant.beneficiaryCategory,
          hostelstatus: applicant.beneficiaryCategory,
        }
      : {}),
    ...(applicant.gender
      ? {
          studentgender: applicant.gender,
          applicantgender: applicant.gender,
          genderoftheapplicant: applicant.gender,
          genderofthestudent: applicant.gender,
        }
      : {}),
    ...(applicant.disability
      ? {
          doyouhaveanytypeofdisability: applicant.disability,
          doYouHaveAnyTypeOfDisability: applicant.disability,
          personwithdisability: applicant.disability,
          personWithDisability: applicant.disability,
          disabilityofanytype: applicant.disability,
          disabilityOfAnyType: applicant.disability,
        }
      : {}),
    ...(typeof applicant.familyAnnualIncome === "number"
      ? {
          familyannualincome: applicant.familyAnnualIncome,
          annualfamilyincome: applicant.familyAnnualIncome,
          annualFamilyIncome: applicant.familyAnnualIncome,
          familyincome: applicant.familyAnnualIncome,
          familyIncome: applicant.familyAnnualIncome,
          annualfamilyincomeinr: applicant.familyAnnualIncome,
        }
      : {}),
    ...(applicant.alpaBhuOrRegisteredLabour
      ? {
          alpabhuorregisteredlabour: applicant.alpaBhuOrRegisteredLabour,
          areyourparentsalpabhudharakshetkari:
            applicant.alpaBhuOrRegisteredLabour,
          areYourParentsAlpaBhudharakShetkari:
            applicant.alpaBhuOrRegisteredLabour,
          areyourparentsregisteredlabour:
            applicant.alpaBhuOrRegisteredLabour,
          areYourParentsRegisteredLabour:
            applicant.alpaBhuOrRegisteredLabour,
          whethereitherparentisaalpabhudharakshetkariregisteredlabour:
            applicant.alpaBhuOrRegisteredLabour,
        }
      : {}),
    ...(applicant.hazardousOrUncleanOccupation
      ? {
          hazardousoruncleanoccupation: applicant.hazardousOrUncleanOccupation,
          isyourfatherormotherengagedinuncleanorhazardousoccupations:
            applicant.hazardousOrUncleanOccupation,
          isYourFatherOrMotherEngagedInUncleanOrHazardousOccupations:
            applicant.hazardousOrUncleanOccupation,
          whethereitherparentundertakeshazardousuncleanoccupation:
            applicant.hazardousOrUncleanOccupation,
        }
      : {}),
    ...(applicant.birthOrder
      ? {
          birthorder: applicant.birthOrder,
          childnumberinfamily: applicant.birthOrder,
          childNumberInFamily: applicant.birthOrder,
          applicantsbirthorderwithinthefamily: applicant.birthOrder,
          applicantBirthOrderWithinTheFamily: applicant.birthOrder,
        }
      : {}),
    ...(applicant.birthOrderLabel
      ? {
          birthorderlabel: applicant.birthOrderLabel,
          birthOrderLabel: applicant.birthOrderLabel,
        }
      : {}),
    ...(applicant.grade10CompletedFromMaharashtra
      ? { grade10completedfrommaharashtra: applicant.grade10CompletedFromMaharashtra }
      : {}),
    ...(applicant.admissionThroughCET
      ? {
          admissionthroughcet: applicant.admissionThroughCET,
          whetheryouradmissionwasdonethroughthecentraladmissionprocessofcetcellofmaharashtra:
            applicant.admissionThroughCET,
        }
      : {}),
    ...(applicant.completed10thFromGovernmentVidyaniketan
      ? {
          completed10thfromgovernmentvidyaniketan:
            applicant.completed10thFromGovernmentVidyaniketan,
          whetheryoucompletedyour10thgradefromagovernmentvidyaniketanschool:
            applicant.completed10thFromGovernmentVidyaniketan,
        }
      : {}),
    ...(applicant.stateId
      ? {
          stateid: applicant.stateId,
        }
      : {}),
    ...(applicant.stateLabel
      ? {
          state: applicant.stateLabel,
          statelabel: applicant.stateLabel,
        }
      : {}),
    ...(applicant.districtId
      ? {
          districtid: applicant.districtId,
        }
      : {}),
    ...(applicant.districtLabel
      ? {
          district: applicant.districtLabel,
          districtlabel: applicant.districtLabel,
        }
      : {}),
    ...(applicant.talukaId
      ? {
          talukaid: applicant.talukaId,
        }
      : {}),
    ...(applicant.talukaLabel
      ? {
          taluka: applicant.talukaLabel,
          talukalabel: applicant.talukaLabel,
        }
      : {}),
    ...(applicant.institutionName
      ? {
          institutionname: applicant.institutionName,
          institutename: applicant.institutionName,
          collegename: applicant.institutionName,
          schoolname: applicant.institutionName,
          collegenameschoolname: applicant.institutionName,
        }
      : {}),
    ...(applicant.universityName ? { universityname: applicant.universityName } : {}),
    ...(applicant.currentCourseName
      ? { currentcoursename: applicant.currentCourseName }
      : {}),
    ...(applicant.currentCourseNameLabel
      ? {
          currentcoursenamelabel: applicant.currentCourseNameLabel,
          coursename: applicant.currentCourseNameLabel,
        }
      : {}),
    ...(applicant.courseType
      ? {
          coursetype: applicant.courseType,
        }
      : {}),
    ...(applicant.courseTypeLabel
      ? {
          coursetypelabel: applicant.courseTypeLabel,
        }
      : {}),
    ...(applicant.yearOfStudyOrCourseLevel
      ? { yearofstudyorcourselevel: applicant.yearOfStudyOrCourseLevel }
      : {}),
    ...(applicant.yearOfStudyOrCourseLevelLabel
      ? {
          yearofstudyorcourselevellabel: applicant.yearOfStudyOrCourseLevelLabel,
          yearofstudy: applicant.yearOfStudyOrCourseLevelLabel,
          courselevel: applicant.yearOfStudyOrCourseLevelLabel,
        }
      : {}),
    ...(standardGradeValue
      ? {
          standardgrade: standardGradeValue,
          standardGrade: standardGradeValue,
        }
      : {}),
  };
}

export async function fetchSchemeById(schemeId) {
  const id = String(schemeId || "").trim();
  if (!id) return null;

  try {
    const data = await fetchJson(`/o/c/schemeconfigurators/?filter=id eq '${id}'&restrictFields=actions`);
    const items = Array.isArray(data?.items) ? data.items : [];
    return items[0] || null;
  } catch (error) {
    console.error("Failed to fetch scheme by id:", error);
    return null;
  }
}

export async function fetchCollegeById(collegeId) {
  const id = String(collegeId || "").trim();
  if (!id) return null;

  try {
    return await fetchJson(`/o/c/colleges/${encodeURIComponent(id)}?restrictFields=actions`);
  } catch (error) {
    console.error("Failed to fetch college by id:", error);
    return null;
  }
}

const objectRestContextPathCache = new Map();

async function fetchObjectDefinitionInfo(objectDefinitionId) {
  const id = Number(objectDefinitionId || 0);
  if (!id) return null;

  try {
    return await fetchJson(`/o/object-admin/v1.0/object-definitions/${id}`);
  } catch (error) {
    console.error("Failed to fetch object definition info:", error);
    return null;
  }
}

async function getRestContextPathForObjectDefinition(objectDefinitionId) {
  const id = Number(objectDefinitionId || 0);
  if (!id) return null;
  if (objectRestContextPathCache.has(id)) return objectRestContextPathCache.get(id);

  const info = await fetchObjectDefinitionInfo(id);
  const restContextPath = normalizeString(info?.restContextPath);
  objectRestContextPathCache.set(id, restContextPath);
  return restContextPath;
}

export async function checkUserAlreadyAppliedForScheme(
  targetObjectDefinitionId,
  loginUserId
) {
  const restContextPath = await getRestContextPathForObjectDefinition(
    targetObjectDefinitionId
  );
  if (!restContextPath || !loginUserId) {
    return { items: [] };
  }

  try {
    const filter = `loginuserid eq ${Number(loginUserId)}`;
    const url = `${restContextPath}?filter=${encodeURIComponent(filter)}`;
    const data = await fetchJson(url);
    return data || { items: [] };
  } catch (error) {
    console.error("Failed to check already applied:", error);
    return { items: [] };
  }
}

export async function fetchDepartmentsOptions() {
  try {
    const items = await fetchAllObjectEntries("departments", {
      sort: "name:asc",
      pageSize: DEFAULT_PAGE_SIZE,
    });

    return items
      .map((d) => ({
        id: String(d?.id ?? ""),
        name: String(d?.name ?? ""),
      }))
      .filter((x) => x.id && x.name);
  } catch (error) {
    console.error("fetchDepartmentsOptions failed:", error);
    return [];
  }
}

export async function fetchAcademicYearOptions() {
  try {
    const items = await fetchPicklistEntries("DBT-ACADEMIC-YEAR");
    return (items || [])
      .map((item) => ({
        value: String(item?.key ?? item?.id ?? item?.value ?? "").trim(),
        label: String(item?.name ?? item?.label ?? item?.key ?? item?.value ?? "")
          .trim(),
      }))
      .filter((x) => x.value && x.label);
  } catch (error) {
    console.error("fetchAcademicYearOptions failed:", error);
    return [];
  }
}

export async function fetchBeneficiaryCategoryOptions() {
  return [
    { value: "Hosteller", label: "Hosteller" },
    { value: "Day Scholar", label: "Day Scholar" },
  ];
}

export async function fetchQualificationLevelOptions() {
  try {
    const items = await fetchAllObjectEntries("qualificationtypes", {
      sort: "name:asc",
      pageSize: 500,
      useLiferayAuth: true,
    });
    return sortByLabel(uniqueByValue(
      (items || [])
        .filter(
          (item) =>
            item?.name &&
            item?.isActive !== false &&
            (item?.status?.code === undefined || Number(item.status.code) === 0)
        )
        .map((item) => ({
          value: String(item?.id ?? "").trim(),
          label: String(item?.name ?? "").trim(),
        }))
    ));
  } catch (error) {
    console.error("fetchQualificationLevelOptions failed:", error);
    return [];
  }
}

export async function fetchBirthOrderOptions() {
  try {
    const items = await fetchPicklistEntries("dbt-child-number-in-family");
    return (items || [])
      .map((item) => ({
        value: String(item?.key ?? item?.id ?? item?.value ?? "").trim(),
        label: String(item?.name ?? item?.label ?? item?.key ?? item?.value ?? "")
          .trim(),
      }))
      .filter((o) => o.value && o.label);
  } catch (error) {
    console.error("fetchBirthOrderOptions failed:", error);
    return [];
  }
}

export async function checkSchemeApplyEligibility(schemeId, formData) {
  const scheme = await fetchSchemeById(schemeId);
  if (!scheme) {
    return { ok: false, reason: "scheme_not_found" };
  }

  const loginUserId = Number(window?.Liferay?.ThemeDisplay?.getUserId?.() || 0);
  const targetObjectDefinitionId = Number(
    scheme?.targetObjectDefinitionId ||
      scheme?.targetobjectdefinitionid ||
      scheme?.targetObjectId ||
      0
  );

  if (targetObjectDefinitionId && loginUserId) {
    const alreadyApplied = await checkUserAlreadyAppliedForScheme(
      targetObjectDefinitionId,
      loginUserId
    );
    if (Array.isArray(alreadyApplied?.items) && alreadyApplied.items.length > 0) {
      return { ok: false, reason: "already_applied", alreadyApplied: true };
    }
  }

  const applicantPayload = mapApplicantPayload(formData);

  let drlContent = normalizeString(scheme?.drl)
    ? normalizeDrlContent(scheme.drl)
    : normalizeString(scheme?.drlContent)
      ? normalizeDrlContent(scheme.drlContent)
      : "";

  const rawRulesApply = getRawRulesForDrlGeneration(scheme);
  if (!drlContent && rawRulesApply) {
    const generated = await generateDrlFromRules(rawRulesApply);
    drlContent = normalizeDrlContent(generated);
  }

  if (!drlContent) {
    if (schemeRulesContradictApplicantPayload(scheme, applicantPayload)) {
      return { ok: false, reason: "not_eligible" };
    }
    logParsedSchemeRulesAfterDrlValidation(scheme, applicantPayload);
    return { ok: true, reason: "eligible" };
  }

  // const validationResult = await validateDrlContent(applicantPayload, drlContent);
  // const eligible = isDrlValidationSuccessful(validationResult);

  // if (!eligible) {
  //   return {
  //     ok: false,
  //     reason: "not_eligible",
  //   };
  // }

  if (schemeRulesContradictApplicantPayload(scheme, applicantPayload)) {
    return {
      ok: false,
      reason: "not_eligible",
    };
  }

  logParsedSchemeRulesAfterDrlValidation(scheme, applicantPayload);

  return {
    ok: true,
    reason: "eligible",
  };
}

export async function validateSchemeEligibilityFromSchemeData(scheme, formData) {
  try {
    if (!scheme) {
      return { ok: false, reason: "scheme_not_found" };
    }

    const applicantPayload = mapApplicantPayload(formData);

    let drlContent = normalizeString(scheme?.drl)
      ? normalizeDrlContent(scheme.drl)
      : normalizeString(scheme?.drlContent)
        ? normalizeDrlContent(scheme.drlContent)
        : "";

    const rawRulesValidate = getRawRulesForDrlGeneration(scheme);
    if (!drlContent && rawRulesValidate) {
      const generated = await generateDrlFromRules(rawRulesValidate);
      drlContent = normalizeDrlContent(generated);
    }

    if (!drlContent) {
      const eligible = !schemeRulesContradictApplicantPayload(scheme, applicantPayload);
      if (eligible) {
        logParsedSchemeRulesAfterDrlValidation(scheme, applicantPayload);
      }
      return {
        ok: true,
        eligible,
        validationResult: null,
      };
    }

    const validationResult = await validateDrlContent(applicantPayload, drlContent);
    // const drlEligible = isDrlValidationSuccessful(validationResult);
    // const eligible =
    //   drlEligible && !schemeRulesContradictApplicantPayload(scheme, applicantPayload);

    // if (eligible) {
    //   logParsedSchemeRulesAfterDrlValidation(scheme, applicantPayload);
    // }

    return {
      ok: true,
      // eslint-disable-next-line no-undef
      eligible,
      validationResult,
    };
  } catch (error) {
    console.error("validateSchemeEligibilityFromSchemeData failed:", error);
    return { ok: false, reason: "validation_failed" };
  }
}

function isEligibleSchemeConfigurator(item, options = {}) {
  // const skipCategoryMatches = options?.skipCategoryMatches === true;
  // const categoryRaw = normalizeString(
  //   item?.schemeCategory || item?.schemecategory || item?.category
  // );
  // const category = categoryRaw.toLowerCase();
  const statusRaw = normalizeString(
    item?.status?.label ||
      item?.status?.label_i18n ||
      item?.status ||
      item?.approvalStatus?.label ||
      item?.approvalStatus
  );
  const statusCode = Number(item?.status?.code ?? item?.approvalStatus?.code ?? NaN);
  const status = statusRaw.toLowerCase();
  const schemeStatusRaw = normalizeString(
    item?.schemeStatus?.label ||
      item?.schemeStatus?.label_i18n ||
      item?.schemeStatus ||
      item?.schemestatus
  );
  const schemeStatusCode = Number(item?.schemeStatus?.code ?? item?.schemestatus?.code ?? NaN);
  const schemeStatus = schemeStatusRaw.toLowerCase();
  const targetObjectDefinitionId = Number(
    item?.targetObjectDefinitionId ||
      item?.targetobjectdefinitionid ||
      item?.targetObjectId ||
      0
  );

  // const categoryMatches =
  //   category === "citizen-driven applications with departmental scrutiny";

  const isSchemeActive =
    schemeStatus === "active" || schemeStatusCode === 0;

  const isApproved =
    status === "approved" || statusCode === 0;

  return (
    // (skipCategoryMatches || categoryMatches) &&
    isSchemeActive &&
    isApproved &&
    targetObjectDefinitionId !== 0
  );
}

const EXCLUDED_KYE_SCHEME_TYPES = new Set(["farmerSchemes"]);

function isExcludedKnowYourEligibilityScheme(item) {
  if (!item || typeof item !== "object") {
    return false;
  }

  if (EXCLUDED_KYE_SCHEME_TYPES.has(getSchemeTypeFromScheme(item))) {
    return true;
  }

  const searchableTokens = [
    item?.schemeType,
    item?.schemetype,
    item?.typeOfScheme,
    item?.typeofscheme,
    item?.schemeCategory,
    item?.schemecategory,
    item?.category,
    item?.departmentName,
    item?.departmentLabel,
    item?.department,
    item?.schemeName,
    item?.name,
    item?.objectFolderExternalReferenceCode,
    item?.objectfolderexternalreferencecode,
  ]
    .map((value) => normalizeString(value).toLowerCase())
    .filter(Boolean);

  return searchableTokens.some(
    (value) => value.includes("farmer") || value.includes("agriculture")
  );
}

function normalizeSchemeTypeToken(value) {
  const normalized = normalizeString(value).toLowerCase().replace(/\s+/g, "");
  if (!normalized) return "";
  if (normalized.includes("otherschemes")) return "specialAssistanceSchemes";
  if (normalized.includes("postmatric")) return "postMatric";
  if (
    normalized.includes("prematric") ||
    normalized.includes("premetric") ||
    normalized.includes("schoolstudent")
  ) {
    return "preMatric";
  }
  if (
    normalized.includes("pension") ||
    normalized.includes("sas") ||
    normalized.includes("pensioner")
  ) {
    return "pensionSchemes";
  }
  if (
    normalized.includes("farmer")
  ) {
    return "farmerSchemes";
  }
  if (
    normalized.includes("specialassistance") ||
    normalized.includes("assistanceschemes") ||
    (normalized.includes("assistance") &&
      !normalized.includes("farmer") &&
      !normalized.includes("agriculture"))
  ) {
    return "specialAssistanceSchemes";
  }
  return "";
}

function stringifySchemeTypeCandidate(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string" || typeof raw === "number") {
    return String(raw);
  }
  if (typeof raw === "boolean") {
    return "";
  }
  if (Array.isArray(raw)) {
    return raw
      .map((x) => stringifySchemeTypeCandidate(x))
      .filter(Boolean)
      .join(" ");
  }
  if (typeof raw === "object") {
    const s =
      raw.name ??
      raw.label ??
      raw.label_i18n ??
      raw.key ??
      raw.value ??
      raw.title ??
      "";
    return normalizeString(s);
  }
  return "";
}

function getSchemeTypeFromScheme(item) {
  const candidates = [
    item?.schemeType,
    item?.schemetype,
    item?.typeOfScheme,
    item?.typeofscheme,
    item?.schemeCategory,
    item?.schemecategory,
    item?.category,
    item?.objectFolderExternalReferenceCode,
    item?.objectfolderexternalreferencecode,
    item?.objectFolder?.externalReferenceCode,
    item?.objectfolder?.externalreferencecode,
    item?.schemeName,
    item?.name,
  ];

  for (const candidate of candidates) {
    const asString = stringifySchemeTypeCandidate(candidate);
    if (!asString) continue;
    const normalizedType = normalizeSchemeTypeToken(asString);
    if (normalizedType) {
      return normalizedType;
    }
  }

  return "";
}

function expandSchemeTypeSelection(selectedTypes) {
  const set = new Set((selectedTypes || []).filter(Boolean));

  if (set.has("pensionSchemes") || set.has("specialAssistanceSchemes")) {
    set.add("pensionSchemes");
    set.add("specialAssistanceSchemes");
  }

  return Array.from(set);
}

function getSelectedSchemeType(formData) {
  const rawValue = formData?.schemeType;

  const normalized = Array.isArray(rawValue)
    ? rawValue
        .map((value) => normalizeString(value))
        .map((value) => normalizeSchemeTypeToken(value))
        .filter(Boolean)
    : (() => {
        const singleValue = normalizeSchemeTypeToken(rawValue);
        return singleValue ? [singleValue] : [];
      })();

  return expandSchemeTypeSelection(normalized);
}

function hasUsableRules(rules) {
  if (!rules) return false;
  if (Array.isArray(rules)) return rules.length > 0;
  if (typeof rules === "object") return Object.keys(rules).length > 0;
  if (typeof rules === "string") {
    const trimmed = rules.trim();
    if (!trimmed) return false;
    const parsed = parseJsonSafe(trimmed);
    if (Array.isArray(parsed)) return parsed.length > 0;
    if (parsed && typeof parsed === "object") return Object.keys(parsed).length > 0;
    return true;
  }
  return false;
}

function parseSchemeRules(rules) {
  if (rules == null) return null;
  if (typeof rules === "object") return rules;
  if (typeof rules === "string") {
    const trimmed = rules.trim();
    if (!trimmed) return null;
    return parseJsonSafe(trimmed);
  }
  return null;
}

function parseSchemeRulesFromScheme(scheme) {
  return parseSchemeRules(scheme?.rules) || parseSchemeRules(scheme?.eligibilityRules);
}

function getRawRulesForDrlGeneration(scheme) {
  if (hasUsableRules(scheme?.rules)) return scheme.rules;
  if (hasUsableRules(scheme?.eligibilityRules)) return scheme.eligibilityRules;
  return null;
}

function normalizeKeyTokenForMatch(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function isOpaqueFieldId(field) {
  const s = String(field || "").trim();
  if (!s) return true;
  if (s.length >= 18 && /^[a-z0-9]+$/i.test(s)) return true;
  return false;
}

function collectRuleFieldCandidates(rule) {
  const out = [];
  const push = (v) => {
    const t = normalizeString(v);
    if (t) out.push(t);
  };
  push(rule.sourceFieldName);
  push(rule.finalFieldName);
  if (!isOpaqueFieldId(rule.field)) {
    push(rule.field);
  }
  return [...new Set(out)];
}


function isCasteCategoryRule(rule) {
  const candidates = collectRuleFieldCandidates(rule).map(normalizeKeyTokenForMatch);
  const attributeBlob = normalizeKeyTokenForMatch(
    [rule?.attributename, rule?.groupName].filter(Boolean).join(" "),
  );

  return (
    candidates.includes("castecategory") ||
    attributeBlob.includes("castecategory")
  );
}

function isBeneficiaryCategoryRule(rule) {
  const candidates = collectRuleFieldCandidates(rule).map(normalizeKeyTokenForMatch);
  const attributeBlob = normalizeKeyTokenForMatch(
    [rule?.attributename, rule?.groupName].filter(Boolean).join(" "),
  );

  return (
    candidates.includes("beneficiarycategory") ||
    attributeBlob.includes("beneficiarycategory")
  );
}

function isDisabilityRule(rule) {
  const candidates = collectRuleFieldCandidates(rule).map(normalizeKeyTokenForMatch);

  const attributeBlob = normalizeKeyTokenForMatch(
    [rule?.attributename, rule?.groupName].filter(Boolean).join(" "),
  );

  return (
    candidates.includes("disability") ||
    candidates.includes("disabilityofanytype") ||
    candidates.includes("disabilityofanytypeyesno") ||
    candidates.includes("doyouhaveanytypeofdisability") ||
    candidates.includes("personwithdisability") ||
    attributeBlob.includes("disability") ||
    attributeBlob.includes("personwithdisability")
  );
}

function isDisabilityPercentageRule(rule) {
  const candidates = collectRuleFieldCandidates(rule).map(normalizeKeyTokenForMatch);
  return candidates.includes("disabilitypercentage");
}

function ruleUsesTextualValues(ruleValue) {
  const values = Array.isArray(ruleValue) ? ruleValue : [ruleValue];

  return values.some((value) => {
    const normalized = normalizeString(value);
    if (!normalized) return false;
    return !/^\d+$/.test(normalized);
  });
}

function getPreferredLabelPayloadKeyForRule(rule, applicantPayload) {
  const candidates = collectRuleFieldCandidates(rule).map(normalizeKeyTokenForMatch);
  const attributeBlob = normalizeKeyTokenForMatch(
    [rule?.attributename, rule?.groupName].filter(Boolean).join(" "),
  );
  const wantsTextualValue = ruleUsesTextualValues(rule?.value);

  if (!wantsTextualValue) {
    return null;
  }

  const has = (key) => Boolean(applicantPayload?.[key]);
  const matches = (...tokens) =>
    tokens.some(
      (token) =>
        candidates.includes(token) || attributeBlob.includes(token),
    );

  if (isCasteCategoryRule(rule) && has("casteCategoryLabel")) {
    return "casteCategoryLabel";
  }
  if (isBeneficiaryCategoryRule(rule)) {
    if (
      matches("beneficiarycategory", "hosteller", "hostelstatus") &&
      has("beneficiaryCategory")
    ) {
      return "beneficiaryCategory";
    }
  }
  if (matches("stateid", "state") && has("stateLabel")) {
    return "stateLabel";
  }
  if (matches("districtid", "district") && has("districtLabel")) {
    return "districtLabel";
  }
  if (matches("talukaid", "taluka") && has("talukaLabel")) {
    return "talukaLabel";
  }
  if (
    matches(
      "disability",
      "disabilityofanytype",
      "disabilityofanytypeyesno",
      "doyouhaveanytypeofdisability",
      "personwithdisability",
    ) &&
    has("disability")
  ) {
    return "disability";
  }
  if (
    matches(
      "institutionname",
      "institutename",
      "collegename",
      "schoolname",
      "collegenameschoolname",
    ) &&
    has("institutionName")
  ) {
    return "institutionName";
  }
  if (matches("currentcoursename", "coursename") && has("currentCourseNameLabel")) {
    return "currentCourseNameLabel";
  }
  if (matches("coursetype") && has("courseTypeLabel")) {
    return "courseTypeLabel";
  }
  if (
    matches(
      "birthorder",
      "applicantsbirthorderwithinthefamily",
      "applicantbirthorderwithinthefamily",
      "childnumberinfamily",
    ) &&
    has("birthOrderLabel")
  ) {
    return "birthOrderLabel";
  }
  if (
    matches("yearofstudyorcourselevel", "yearofstudy", "courselevel","qualificationlevel") &&
    has("yearOfStudyOrCourseLevelLabel")
  ) {
    return "yearOfStudyOrCourseLevelLabel";
  }

  return null;
}


function attributeNameToPayloadKey(rule, applicantPayload) {
  const blob = normalizeString(
    [
      ...collectRuleFieldCandidates(rule),
      rule.attributename,
      rule.groupName,
    ].filter(Boolean).join(" "),
  ).toLowerCase();
  if (!blob || !applicantPayload) return null;

  const pick = (key) =>
    Object.prototype.hasOwnProperty.call(applicantPayload, key) ? key : null;

  if (blob.includes("domicile") && (blob.includes("maharashtra") || blob.includes("karnataka"))) {
    return pick("domicileOfMaharashtra");
  }
  if (blob.includes("caste") && blob.includes("category")) {
    return pick("casteCategory");
  }
  if (blob.includes("gender")) {
    return pick("gender");
  }
  if (blob.includes("disability") || blob.includes("person with disability")) {
    return pick("disability");
  }
  if (
    (blob.includes("annual") && blob.includes("income")) ||
    blob.includes("family income") ||
    blob.includes("familyannual")
  ) {
    return pick("familyAnnualIncome");
  }
  if (
    (blob.includes("alpa bhu") || blob.includes("bhu-dharak")) &&
    blob.includes("registered labour")
  ) {
    return pick("alpaBhuOrRegisteredLabour");
  }
  if (
    (blob.includes("hazardous") || blob.includes("unclean")) &&
    blob.includes("occupation")
  ) {
    return pick("hazardousOrUncleanOccupation");
  }
  if (blob.includes("alpa bhu") || blob.includes("bhu-dharak")) {
    return pick("alpaBhuOrRegisteredLabour");
  }
  if (blob.includes("registered labour") || blob.includes("registered labor")) {
    return pick("alpaBhuOrRegisteredLabour");
  }
  if (
    blob.includes("birth order") ||
    (blob.includes("child number") && blob.includes("family"))
  ) {
    return pick("birthOrder");
  }
  if (blob.includes("central admission process") || blob.includes("cet cell")) {
    return pick("admissionThroughCET");
  }
  if (blob.includes("government vidyaniketan")) {
    return pick("completed10thFromGovernmentVidyaniketan");
  }
  if (blob.includes("beneficiary") && blob.includes("category")) {
    return pick("beneficiaryCategory");
  }
  if (blob === "state" || blob.includes(" state ")) {
    return pick("stateLabel") || pick("stateId");
  }
  if (blob === "district" || blob.includes(" district ")) {
    return pick("districtLabel") || pick("districtId");
  }
  if (blob === "taluka" || blob.includes(" taluka ")) {
    return pick("talukaLabel") || pick("talukaId");
  }
  if (
    blob.includes("institute name") ||
    blob.includes("institution name") ||
    blob.includes("college name") ||
    blob.includes("school name") ||
    blob.includes("college name/school name") ||
    blob.includes("college/school name")
  ) {
    return pick("institutionName");
  }
  if (blob.includes("current course name") || blob.includes("course name")) {
    return pick("currentCourseNameLabel") || pick("currentCourseName");
  }
  if (blob.includes("course type")) {
    return pick("courseTypeLabel") || pick("courseType");
  }
  if (blob.includes("standardgrade") || blob.includes("standard/grade")) {
    return pick("standardgrade") || pick("yearOfStudyOrCourseLevelLabel") || pick("yearOfStudyOrCourseLevel");
  }
  if (blob.includes("year of study") || blob.includes("course level")) {
    return pick("yearOfStudyOrCourseLevelLabel") || pick("yearOfStudyOrCourseLevel");
  }
  return null;
}


function findPayloadKeyForRule(rule, applicantPayload) {
  const candidates = collectRuleFieldCandidates(rule);
  const keys = Object.keys(applicantPayload || {});
  const lowerToKey = new Map(keys.map((k) => [k.toLowerCase(), k]));
  const normToKey = new Map(keys.map((k) => [normalizeKeyTokenForMatch(k), k]));

  const preferredLabelKey = getPreferredLabelPayloadKeyForRule(rule, applicantPayload);
  if (preferredLabelKey) {
    return preferredLabelKey;
  }

  if (candidates.length > 0) {
    for (const c of candidates) {
      if (Object.prototype.hasOwnProperty.call(applicantPayload, c)) return c;
      const byLower = lowerToKey.get(c.toLowerCase());
      if (byLower) return byLower;
      const byNorm = normToKey.get(normalizeKeyTokenForMatch(c));
      if (byNorm) return byNorm;
    }

    const MIN_INCLUDES_LEN = 8;
    for (const c of candidates) {
      const cn = normalizeKeyTokenForMatch(c);
      if (cn.length < MIN_INCLUDES_LEN) continue;
      for (const k of keys) {
        const kn = normalizeKeyTokenForMatch(k);
        if (kn.length < MIN_INCLUDES_LEN) continue;
        if (kn.includes(cn) || cn.includes(kn)) {
          return k;
        }
      }
    }
  }

  return attributeNameToPayloadKey(rule, applicantPayload);
}

function evaluateLeafCondition(condition, ruleValue, actual) {
  const c = normalizeString(condition).toLowerCase();
  if (c === "isempty") {
    if (Array.isArray(actual)) {
      return actual.filter((value) => normalizeString(value) !== "").length === 0;
    }
    return normalizeString(actual) === "";
  }
  if (c === "isnotempty") {
    if (Array.isArray(actual)) {
      return actual.filter((value) => normalizeString(value) !== "").length > 0;
    }
    return normalizeString(actual) !== "";
  }
  if (c === "istrue") {
    const a = normalizeString(actual).toLowerCase();
    const e = normalizeString(ruleValue).toLowerCase();
    if (a === e) return true;
    if (e === "yes") {
      return a === "yes" || a === "true" || a === "1";
    }
    if (e === "no") {
      return a === "no" || a === "false" || a === "0";
    }
    return false;
  }
  if (c === "isfalse") {
    const a = normalizeString(actual).toLowerCase();
    const e = normalizeString(ruleValue).toLowerCase();
    if (a === e) return true;
    if (e === "no") {
      return a === "no" || a === "false" || a === "0";
    }
    if (e === "yes") {
      return a === "yes" || a === "true" || a === "1";
    }
    if (e === "false") {
      return a === "false" || a === "no" || a === "0";
    }
    if (e === "true") {
      return a === "true" || a === "yes" || a === "1";
    }
    return false;
  }
  if (c === "equals") {
    const vals = Array.isArray(ruleValue) ? ruleValue : [ruleValue];
    const a = normalizeString(actual);
    const aLower = a.toLowerCase();
    return vals.some((v) => {
      const nv = normalizeString(v);
      return nv === a || nv.toLowerCase() === aLower;
    });
  }
  if (c === "contains") {
    const actualValues = Array.isArray(actual) ? actual : [actual];
    const expectedValues = Array.isArray(ruleValue) ? ruleValue : [ruleValue];

    const normalizedActualValues = actualValues
      .map((value) => normalizeString(value).toLowerCase())
      .filter(Boolean);
    const normalizedExpectedValues = expectedValues
      .map((value) => normalizeString(value).toLowerCase())
      .filter(Boolean);

    if (!normalizedActualValues.length || !normalizedExpectedValues.length) {
      return false;
    }

    return normalizedActualValues.some((actualValue) =>
      normalizedExpectedValues.some((expectedValue) =>
        actualValue.includes(expectedValue),
      ),
    );
  }
  if (c === "notequals" || c === "not_equal" || c === "not_equals") {
    const vals = Array.isArray(ruleValue) ? ruleValue : [ruleValue];
    const a = normalizeString(actual);
    const aLower = a.toLowerCase();
    return vals.every((v) => {
      const nv = normalizeString(v);
      return nv !== a && nv.toLowerCase() !== aLower;
    });
  }
  if (
    c === "notmorethan" ||
    c === "not_more_than" ||
    c === "lessthanorequal" ||
    c === "less_than_or_equal" ||
    c === "less_than_or_equals" ||
    c === "less_than_equal"
  ) {
    return Number(actual) <= Number(ruleValue);
  }
  if (
    c === "greaterthanorequal" ||
    c === "greater_than_or_equal" ||
    c === "greater_than_or_equals" ||
    c === "greater_than_equal"
  ) {
    return Number(actual) >= Number(ruleValue);
  }
  if (c === "lessthan" || c === "less_than") {
    return Number(actual) < Number(ruleValue);
  }
  if (c === "greaterthan" || c === "greater_than") {
    return Number(actual) > Number(ruleValue);
  }
  return false;
}

function isEmptyCondition(condition) {
  return normalizeString(condition).toLowerCase() === "isempty";
}

function isNotEmptyCondition(condition) {
  return normalizeString(condition).toLowerCase() === "isnotempty";
}

function isBirthOrderRuleKey(key) {
  const normalized = normalizeKeyTokenForMatch(key);
  return (
    normalized === "birthorder" ||
    normalized === "applicantsbirthorderwithinthefamily" ||
    normalized === "applicantbirthorderwithinthefamily" ||
    normalized === "childnumberinfamily"
  );
}

function normalizeBirthOrderComparableValue(value) {
  const normalized = normalizeKeyTokenForMatch(value);

  if (!normalized) {
    return "";
  }
  if (
    normalized === "first" ||
    normalized === "firstchild" ||
    normalized === "1" ||
    normalized === "1st"
  ) {
    return "firstchild";
  }
  if (
    normalized === "second" ||
    normalized === "secondchild" ||
    normalized === "2" ||
    normalized === "2nd"
  ) {
    return "secondchild";
  }
  if (normalized === "other") {
    return "other";
  }

  return normalized;
}

function birthOrderValuesEqual(left, right) {
  return (
    normalizeBirthOrderComparableValue(left) ===
    normalizeBirthOrderComparableValue(right)
  );
}

function isOptionalEmptyBirthOrderRule(condition, ruleValue, actual, key) {
  if (normalizeString(condition).toLowerCase() !== "equals") {
    return false;
  }

  if (!isBirthOrderRuleKey(key)) {
    return false;
  }

  return normalizeString(ruleValue) === "" && normalizeString(actual) !== "";
}

function evaluateLeafConditionWithKey(condition, ruleValue, actual, key) {
  if (isBirthOrderRuleKey(key)) {
    const c = normalizeString(condition).toLowerCase();
    const vals = Array.isArray(ruleValue) ? ruleValue : [ruleValue];

    if (c === "equals") {
      return vals.some((v) => birthOrderValuesEqual(actual, v));
    }

    if (c === "notequals" || c === "not_equal" || c === "not_equals") {
      return vals.every((v) => !birthOrderValuesEqual(actual, v));
    }
  }

  return evaluateLeafCondition(condition, ruleValue, actual);
}

function evaluateRuleNode(node, applicantPayload) {
  if (node == null) {
    return { status: "skipped" };
  }

  if (Array.isArray(node)) {
    const children = node.map((item) => evaluateRuleNode(item, applicantPayload));
    const actionableChildren = children.filter(
      (child) => child.status === "passed" || child.status === "failed",
    );

    if (actionableChildren.length === 0) {
      return { status: "skipped", children };
    }

    return {
      status: actionableChildren.every((child) => child.status === "passed")
        ? "passed"
        : "failed",
      children,
    };
  }

  if (typeof node !== "object") {
    return { status: "skipped" };
  }

  if (node.field && node.condition) {
    // Always skip disability percentage rules
    if (isDisabilityPercentageRule(node)) {
      return { status: "skipped", reason: "disability_percentage_rule_skipped" };
    }

    const key = findPayloadKeyForRule(node, applicantPayload);

    if (key === null) {
      if (isDisabilityRule(node)) {
        return { status: "skipped", reason: "missing_optional_disability_value" };
      }
      return { status: "skipped", reason: "no_matching_payload_field" };
    }

    const actual = applicantPayload[key];
    if (actual === undefined || actual === null) {
      if (isDisabilityRule(node)) {
        return { status: "skipped", reason: "missing_optional_disability_value", key };
      }
      if (isEmptyCondition(node.condition)) {
        return {
          status: "passed",
          key,
          actual,
          expected: node.value,
          condition: node.condition,
        };
      }
      if (isNotEmptyCondition(node.condition)) {
        return {
          status: "failed",
          key,
          actual,
          expected: node.value,
          condition: node.condition,
        };
      }
      return { status: "skipped", reason: "no_payload_value", key };
    }

    if (isOptionalEmptyBirthOrderRule(node.condition, node.value, actual, key)) {
      return {
        status: "skipped",
        reason: "optional_empty_birth_order_rule",
        key,
        actual,
        expected: node.value,
        condition: node.condition,
      };
    }

    return {
      status: evaluateLeafConditionWithKey(node.condition, node.value, actual, key)
        ? "passed"
        : "failed",
      key,
      actual,
      expected: node.value,
      condition: node.condition,
    };
  }

  const children = [];

  if (Array.isArray(node.rules)) {
    children.push(...node.rules.map((child) => evaluateRuleNode(child, applicantPayload)));
  }

  if (Array.isArray(node.groups)) {
    children.push(...node.groups.map((child) => evaluateRuleNode(child, applicantPayload)));
  }

  const actionableChildren = children.filter(
    (child) => child.status === "passed" || child.status === "failed",
  );

  if (actionableChildren.length === 0) {
    return { status: "skipped", children };
  }

  const condition = normalizeString(node.condition || "AND").toUpperCase();
  const passed =
    condition === "OR"
      ? actionableChildren.some((child) => child.status === "passed")
      : actionableChildren.every((child) => child.status === "passed");

  return {
    status: passed ? "passed" : "failed",
    condition,
    children,
  };
}


function collectSchemeRuleLeaves(node, pathParts = []) {
  if (node == null) return [];
  if (Array.isArray(node)) {
    return node.flatMap((n, i) => collectSchemeRuleLeaves(n, [...pathParts, String(i)]));
  }
  if (typeof node !== "object") return [];

  const nested = Array.isArray(node.rules) ? node.rules : [];
  if (nested.length > 0) {
    const label = normalizeString(node.type || node.groupName) || "group";
    return nested.flatMap((child, i) =>
      collectSchemeRuleLeaves(child, [...pathParts, `${label}:${i}`]),
    );
  }

  if (node.field && node.condition) {
    return [{ path: pathParts.filter(Boolean).join(" > "), rule: node }];
  }

  return [];
}


export function evaluateSchemeRulesAgainstPayload(parsedRules, applicantPayload) {
  if (!parsedRules || typeof parsedRules !== "object") {
    return {
      summary: { total: 0, passed: 0, failed: 0, skipped: 0 },
      leaves: [],
    };
  }

  const collected = collectSchemeRuleLeaves(parsedRules);
  const leaves = collected.map(({ path, rule }) => {
    const key = findPayloadKeyForRule(rule, applicantPayload);
    if (key === null) {
      if (isDisabilityRule(rule)) {
        return {
          path,
          status: "skipped",
          reason: "missing_optional_disability_value",
          attributename: rule.attributename,
          fieldCandidates: collectRuleFieldCandidates(rule),
        };
      }
      return {
        path,
        status: "skipped",
        reason: "no_matching_payload_field",
        attributename: rule.attributename,
        fieldCandidates: collectRuleFieldCandidates(rule),
      };
    }

    const actual = applicantPayload[key];
    if (actual === undefined || actual === null) {
      if (isEmptyCondition(rule.condition)) {
        return {
          path,
          status: "passed",
          key,
          condition: rule.condition,
          expected: rule.value,
          actual,
          attributename: rule.attributename,
          groupName: rule.groupName,
        };
      }
      if (isNotEmptyCondition(rule.condition)) {
        return {
          path,
          status: "failed",
          key,
          condition: rule.condition,
          expected: rule.value,
          actual,
          attributename: rule.attributename,
          groupName: rule.groupName,
        };
      }
      if (isDisabilityRule(rule)) {
        return {
          path,
          status: "failed",
          reason: "missing_required_disability_value",
          key,
          attributename: rule.attributename,
        };
      }
      return {
        path,
        status: "skipped",
        reason: "no_payload_value",
        key,
        attributename: rule.attributename,
      };
    }

    if (isOptionalEmptyBirthOrderRule(rule.condition, rule.value, actual, key)) {
      return {
        path,
        status: "skipped",
        reason: "optional_empty_birth_order_rule",
        key,
        condition: rule.condition,
        expected: rule.value,
        actual,
        attributename: rule.attributename,
        groupName: rule.groupName,
      };
    }

    const passed = evaluateLeafConditionWithKey(rule.condition, rule.value, actual, key);
    return {
      path,
      status: passed ? "passed" : "failed",
      key,
      condition: rule.condition,
      expected: rule.value,
      actual,
      attributename: rule.attributename,
      groupName: rule.groupName,
    };
  });

  return {
    summary: {
      total: leaves.length,
      passed: leaves.filter((r) => r.status === "passed").length,
      failed: leaves.filter((r) => r.status === "failed").length,
      skipped: leaves.filter((r) => r.status === "skipped").length,
    },
    leaves,
  };
}


function schemeRulesContradictApplicantPayload(scheme, applicantPayload) {
  const parsed = parseSchemeRulesFromScheme(scheme);
  if (!parsed) return false;
  const result = evaluateRuleNode(parsed, applicantPayload);
  return result.status === "failed";
}

function logParsedSchemeRulesAfterDrlValidation(scheme, applicantPayload) {
  const parsed = parseSchemeRulesFromScheme(scheme);
  const vsPayload = evaluateSchemeRulesAgainstPayload(parsed, applicantPayload);
  console.log("[scheme.rules parsed]", { schemeId: scheme?.id, rules: parsed });
  console.log("[scheme.rules vs applicantPayload]", {
    schemeId: scheme?.id,
    summary: vsPayload.summary,
    leaves: vsPayload.leaves,
  });
}

function logFailedSchemeRulesDuringFiltering(scheme, applicantPayload) {
  const parsed = parseSchemeRulesFromScheme(scheme);
  const vsPayload = evaluateSchemeRulesAgainstPayload(parsed, applicantPayload);
  console.log("[scheme excluded] rules vs applicantPayload", {
    schemeId: scheme?.id,
    summary: vsPayload.summary,
    leaves: vsPayload.leaves,
  });
}

async function generateDrlFromRules(rules) {
  console.log("rules:::::::",rules)
  try {
    let parsedRules = rules;
    if (typeof rules === "string") {
      try {
        parsedRules = JSON.parse(rules);
      } catch (parseError) {
        console.error("Invalid JSON in eligibility rules:", parseError);
        return null;
      }
    }
    const normalizedPayload = normalizeRulesForDrlGenerate(parsedRules);

    const response = await fetch(`${DRL_BASE_URL}/api/rules/generate-drl-content-rule`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(normalizedPayload),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate DRL content: ${response.status}`);
    }

    return await response.text();
  } catch (error) {
    console.error("Error calling DRL API:", error);
    return null;
  }
}

function normalizeRulesForDrlGenerate(rawRules) {
  if (!rawRules || typeof rawRules !== "object") {
    return {
      condition: "AND",
      groups: [{ id: "", type: "", condition: "AND", rules: [], actions: [] }],
    };
  }

  return {
    condition: rawRules.condition || "AND",
    groups: [{ id: "", type: "", condition: "AND", rules: [], actions: [] }],
  };
}

async function validateDrlContent(applicantPayload, drlContent) {
  try {
    const payload = {
      user: { data: applicantPayload },
      drlContent: normalizeDrlContent(drlContent),
    };
    const response = await fetch(`${DRL_BASE_URL}/api/validation/user/validate-drl-content`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const text = await response.text();
    const json = parseJsonSafe(text);

    if (response.status === 400) {
      return json || { valid: false };
    }

    if (!response.ok) {
      throw new Error(`Eligibility validation failed: ${response.status}`);
    }

    return json || { valid: false };
  } catch (error) {
    console.error("Error validating DRL content:", error);
    return null;
  }
}

export async function checkEligibilityAgainstSchemeConfigurators(formData, options = {}) {
  const applicantPayload = mapApplicantPayload(formData);
  const selectedSchemeType = getSelectedSchemeType(formData);
  const filteredSchemes = await fetchBrowsableSchemeConfigurators(
    selectedSchemeType,
    { raw: true, skipCategoryMatches: options?.skipCategoryMatches === true },
  );
  const evaluated = await Promise.all(
    filteredSchemes.map(async (scheme) => {
      try {
        let drlContent = normalizeString(scheme?.drl)
          ? normalizeDrlContent(scheme.drl)
          : normalizeString(scheme?.drlContent)
            ? normalizeDrlContent(scheme.drlContent)
            : "";

        const rawRulesList = getRawRulesForDrlGeneration(scheme);
        if (!drlContent && rawRulesList) {
          try {
            drlContent = normalizeDrlContent(await generateDrlFromRules(rawRulesList));
          } catch (error) {
            console.warn("DRL generation failed for scheme:", scheme?.id, error);
          }
        }

        if (!drlContent) {
          if (schemeRulesContradictApplicantPayload(scheme, applicantPayload)) {
            logFailedSchemeRulesDuringFiltering(scheme, applicantPayload);
            return null;
          }
          logParsedSchemeRulesAfterDrlValidation(scheme, applicantPayload);
          return {
            id: String(scheme?.id || ""),
            schemeName: normalizeString(scheme?.schemeName || scheme?.name),
            department: normalizeString(
              scheme?.departmentName || scheme?.department || scheme?.departmentLabel
            ),
            description: normalizeString(
              scheme?.description || scheme?.schemeDescription || scheme?.summary
            ),
            grLink: normalizeString(scheme?.grUrl || scheme?.grLink || scheme?.grDocumentURL),
            raw: scheme,
          };
        }

        // const validationResult = await validateDrlContent(applicantPayload, drlContent);
        // if (!isDrlValidationSuccessful(validationResult)) {
        //   return null;
        // }

        if (schemeRulesContradictApplicantPayload(scheme, applicantPayload)) {
          logFailedSchemeRulesDuringFiltering(scheme, applicantPayload);
          return null;
        }

        logParsedSchemeRulesAfterDrlValidation(scheme, applicantPayload);

        return {
          id: String(scheme?.id || ""),
          schemeName: normalizeString(scheme?.schemeName || scheme?.name),
          department: normalizeString(
            scheme?.departmentName || scheme?.department || scheme?.departmentLabel
          ),
          description: normalizeString(
            scheme?.description || scheme?.schemeDescription || scheme?.summary
          ),
          grLink: normalizeString(scheme?.grUrl || scheme?.grLink || scheme?.grDocumentURL),
          raw: scheme,
        };
      } catch (error) {
        console.error("Eligibility evaluation failed for scheme:", scheme?.schemeName, error);
        return null;
      }
    })
  );

  return evaluated.filter(Boolean);
}

function mapSchemeConfiguratorToEligibilityCard(scheme) {
  return {
    id: String(scheme?.id || ""),
    schemeName: normalizeString(scheme?.schemeName || scheme?.name),
    department: normalizeString(
      scheme?.departmentName || scheme?.department || scheme?.departmentLabel
    ),
    description: normalizeString(
      scheme?.description || scheme?.schemeDescription || scheme?.summary
    ),
    grLink: normalizeString(scheme?.grUrl || scheme?.grLink || scheme?.grDocumentURL),
    benefitAmount: normalizeString(scheme?.benefitAmount || scheme?.benefits || ""),
    raw: scheme,
  };
}

export async function fetchBrowsableSchemeConfigurators(
  selectedSchemeType = [],
  options = {},
) {
  const allSchemes = await fetchAllObjectEntries("schemeconfigurators", {
    sort: "schemeName:asc",
    useLiferayAuth: true,
  });

  const selectedSchemeTypes = (
    Array.isArray(selectedSchemeType)
      ? selectedSchemeType
      : [selectedSchemeType].filter(Boolean)
  ).filter((type) => !EXCLUDED_KYE_SCHEME_TYPES.has(type));

  const filteredSchemes = (allSchemes || []).filter((scheme) => {
    if (!isEligibleSchemeConfigurator(scheme, options)) return false;
    if (isExcludedKnowYourEligibilityScheme(scheme)) return false;
    if (
      selectedSchemeTypes.length > 0 &&
      !selectedSchemeTypes.includes(getSchemeTypeFromScheme(scheme))
    ) {
      return false;
    }
    return true;
  });

  if (options?.raw) {
    return filteredSchemes;
  }

  return filteredSchemes.map(mapSchemeConfiguratorToEligibilityCard);
}

export async function fetchJobTitle(userId) {
  try {
    if (!userId) {
      return 0;
    }

    const res = await fetch(`/o/headless-admin-user/v1.0/user-accounts/${userId}`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        // "x-csrf-token": window.Liferay?.authToken || "",
        "Authorization": "Basic " + btoa("prabhudasu:root"),
      },
      credentials: "include",
    });

    if (!res.ok) {
      console.warn("User not found while fetching Maha ID:", res.status);
      return 0;
    }

    const data = await res.json();
    return data.jobTitle || 0;
  } catch (error) {
    console.error("Error fetching Maha ID:", error);
    return 0;
  }
}

export async function fetchApiDataBaseOnSchemeIdAndMahaId(schemeId = 0, mahaId) {
  try {
    if (!String(mahaId || "").trim()) {
      return null;
    }

    const resp = await fetch("/o/mhdbt-headless-service/v1.0/get-mhucdh-by-mahaid", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        // "x-csrf-token": window.Liferay?.authToken || "",
        "Authorization": "Basic " + btoa("prabhudasu:root"),
      },
      body: JSON.stringify({
        mahaId: String(mahaId),
        schemeId: Number(schemeId || 0),
      }),
      credentials: "include",
    });

    if (!resp.ok) {
      console.error("Failed to fetch MH-UCDH data:", resp.status);
      return null;
    }

    const responseData = await resp.json();
    return responseData?.data ?? null;
  } catch (error) {
    console.error("Error fetching MH-UCDH data by Maha ID:", error);
    return null;
  }
}

async function fetchSearchEntries(objectName, searchValue, sortField = "") {
  if (!objectName) return [];
  if (searchValue === null || searchValue === undefined || searchValue === "") {
    return [];
  }

  const allItems = [];
  let page = 1;
  let pageSize = null;

  while (true) {
    let url = `/o/c/${objectName}/?page=${page}`;

    if (pageSize) {
      url += `&pageSize=${pageSize}`;
    }

    if (sortField) {
      url += `&sort=${sortField}:asc`;
    }

    url += `&search=${encodeURIComponent(String(searchValue))}&restrictFields=actions`;

    const response = await fetch(url, getFetchConfig());
    if (!response.ok) {
      throw new Error(`Failed to fetch ${objectName} by search: ${response.status}`);
    }

    const data = await response.json();
    if (!pageSize) {
      pageSize = data?.pageSize || DEFAULT_PAGE_SIZE;
    }

    allItems.push(...(Array.isArray(data?.items) ? data.items : []));

    if (page >= Number(data?.lastPage || 1)) {
      break;
    }

    page += 1;
  }

  return allItems;
}

function getMatchingCollegeCourseMapping(mappingRows = [], selectedCourseId) {
  if (!Array.isArray(mappingRows) || mappingRows.length === 0) {
    return null;
  }

  if (!selectedCourseId) {
    return mappingRows[0];
  }

  const normalizedSelectedCourseId = String(selectedCourseId).trim();

  return (
    mappingRows.find((item) =>
      [
        item?.r_course_c_courseId,
        item?.r_course_c_courseid,
        item?.courseId,
        item?.r_course_c_course?.id,
      ]
        .filter((value) => value !== null && value !== undefined && value !== "")
        .map((value) => String(value).trim())
        .includes(normalizedSelectedCourseId),
    ) || mappingRows[0]
  );
}

function extractFeeDetailsFromCoursesFee(collegeCourseFeeRow) {
  if (!collegeCourseFeeRow?.coursesFee) {
    return {
      examFee: 0,
      tuitionFee: 0,
    };
  }

  try {
    const parsedCoursesFee =
      typeof collegeCourseFeeRow.coursesFee === "string"
        ? JSON.parse(collegeCourseFeeRow.coursesFee)
        : collegeCourseFeeRow.coursesFee;

    const firstFee = Array.isArray(parsedCoursesFee?.fees)
      ? parsedCoursesFee.fees[0]
      : null;

    return {
      examFee: Number(firstFee?.annualFee) || 0,
      tuitionFee: Number(firstFee?.feeTypeAmount) || 0,
    };
  } catch (error) {
    console.error("Failed to parse college course fee payload:", error);
    return {
      examFee: 0,
      tuitionFee: 0,
    };
  }
}

function normalizeIsProfessionalFromApi(raw) {
  if (raw === true || raw === false) return raw;
  if (typeof raw === "string") {
    const lower = raw.trim().toLowerCase();
    if (lower === "true" || lower === "1") return true;
    if (lower === "false" || lower === "0") return false;
  }
  return Number(raw) === 1;
}


export async function fetchIsProfessionalFromCollegeAndCourse(collegeId, courseId) {
  const collegeIdClean = String(collegeId || "").trim();
  const courseIdClean = String(courseId || "").trim();
  if (!collegeIdClean || !courseIdClean) {
    return false;
  }

  let resolvedCourseId = courseIdClean;

  try {
    const mappings = await fetchSearchEntries(
      "collegecoursemappings",
      collegeIdClean,
      "courseId",
    );
    const matchedMapping = getMatchingCollegeCourseMapping(mappings, courseIdClean);
    const fromMapping =
      matchedMapping?.r_course_c_courseId ??
      matchedMapping?.r_course_c_courseid ??
      matchedMapping?.courseId ??
      matchedMapping?.r_course_c_course?.id;
    if (fromMapping !== null && fromMapping !== undefined && String(fromMapping).trim() !== "") {
      resolvedCourseId = String(fromMapping).trim();
    }
  } catch (error) {
    console.warn("College course mapping lookup for isProfessional failed:", error);
  }

  try {
    const courseItems = await fetchSearchEntries("courses", resolvedCourseId, "name");
    const rows = Array.isArray(courseItems) ? courseItems : [];
    const match =
      rows.find(
        (c) =>
          String(c?.id ?? "").trim() === resolvedCourseId ||
          String(c?.id ?? "").trim() === courseIdClean,
      ) || rows[0];

    if (!match) {
      return false;
    }

    return normalizeIsProfessionalFromApi(match.isProfessional ?? match.isprofessional);
  } catch (error) {
    console.warn("Course isProfessional lookup failed:", error);
    return false;
  }
}

export async function fetchBenefitFeeDetails(collegeId, courseId) {
  const collegeIdClean = String(collegeId || "").trim();
  if (!collegeIdClean) {
    return {
      examFee: 0,
      tuitionFee: 0,
    };
  }

  try {
    const mappings = await fetchSearchEntries(
      "collegecoursemappings",
      collegeIdClean,
      "courseId",
    );

    const matchedMapping = getMatchingCollegeCourseMapping(mappings, courseId);
    const collegeCourseMappingId = matchedMapping?.id;

    if (!collegeCourseMappingId) {
      return {
        examFee: 0,
        tuitionFee: 0,
      };
    }

    let collegeCourseFees = await fetchAllObjectEntries("collegecoursefees", {
      filter: `r_courseCollegeMapping_c_collegeCourseMappingId eq '${collegeCourseMappingId}'`,
    });

    if (!Array.isArray(collegeCourseFees) || collegeCourseFees.length === 0) {
      collegeCourseFees = await fetchSearchEntries(
        "collegecoursefees",
        collegeCourseMappingId,
        "id",
      );
    }

    const firstCollegeCourseFee = Array.isArray(collegeCourseFees)
      ? collegeCourseFees[0]
      : null;

    return extractFeeDetailsFromCoursesFee(firstCollegeCourseFee);
  } catch (error) {
    console.error("Failed to fetch college fee details:", error);
    return {
      examFee: 0,
      tuitionFee: 0,
    };
  }
}

export async function fetchBenefitsCalculation(payload) {
  const baseUrl = DRL_BASE_URL || "";
  if (!baseUrl) {
    throw new Error("Benefits API base URL is not configured (REACT_APP_BASE_URL).");
  }

  try {
    const response = await fetch(`${baseUrl}/api/benefits/user/benefits-calculation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch benefits");
    }

    return await response.json();
  } catch (error) {
    console.error("Benefits API error:", error);
    throw error;
  }
}
